import { findSceneNode, getSceneNodeChildren, type CurvaDocument, type PrototypeLink } from "./document";
import type { FrameNode, SceneNode } from "./nodes";

export function prototypeLinks(document: CurvaDocument): PrototypeLink[] {
  return document.prototype?.links ?? [];
}

export function outgoingPrototypeLink(document: CurvaDocument, sourceNodeId: string): PrototypeLink | undefined {
  return prototypeLinks(document).find(link => link.sourceNodeId === sourceNodeId);
}

export function prototypeLinksForFrame(document: CurvaDocument, frameId: string): PrototypeLink[] {
  const frame = findSceneNode(document, frameId);
  if (frame?.type !== "frame") {
    return [];
  }

  const frameNodeIds = new Set([frame.id, ...descendantNodeIds(frame)]);
  return prototypeLinks(document).filter(link => (
    link.targetFrameId === frame.id || frameNodeIds.has(link.sourceNodeId)
  ));
}

export function framesWithOnlyOutgoingPrototypeLinks(document: CurvaDocument): FrameNode[] {
  const links = prototypeLinks(document);
  const incomingFrameIds = new Set(links.map(link => link.targetFrameId));
  const outgoingSourceIds = new Set(links.map(link => link.sourceNodeId));
  return allFrames(document).filter(frame => {
    const frameNodeIds = new Set([frame.id, ...descendantNodeIds(frame)]);
    return !incomingFrameIds.has(frame.id) && [...frameNodeIds].some(nodeId => outgoingSourceIds.has(nodeId));
  });
}

export function upsertPrototypeLink(document: CurvaDocument, link: PrototypeLink): void {
  const source = findSceneNode(document, link.sourceNodeId);
  const target = findSceneNode(document, link.targetFrameId);
  if (!source) {
    throw new Error(`Prototype source ${link.sourceNodeId} does not exist.`);
  }
  if (target?.type !== "frame") {
    throw new Error("Prototype links can only target frames.");
  }

  const links = prototypeLinks(document).filter(candidate => candidate.sourceNodeId !== link.sourceNodeId);
  document.prototype = { links: [...links, structuredClone(link)] };
}

export function replacePrototypeLinks(document: CurvaDocument, links: PrototypeLink[]): void {
  document.prototype = { links: links.map(link => structuredClone(link)) };
}

export function allFrames(document: CurvaDocument): FrameNode[] {
  return document.pages.flatMap(page => framesInNodes(page.children));
}

function framesInNodes(nodes: SceneNode[]): FrameNode[] {
  return nodes.flatMap(node => {
    const children = getSceneNodeChildren(node);
    const nested = children ? framesInNodes(children) : [];
    return node.type === "frame" ? [node, ...nested] : nested;
  });
}

function descendantNodeIds(node: SceneNode): string[] {
  const children = getSceneNodeChildren(node);
  if (!children) {
    return [];
  }

  return children.flatMap(child => [child.id, ...descendantNodeIds(child)]);
}
